import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { MatchSetResponse, MatchSetsResponse } from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * Two reads and no mutation, which mirrors the API exactly. Saving a match to the meet list is a
 * call into `attendee-preview` — `MEM-025` AC-7's *"the meet list is one list"* — so there is no
 * `useSaveMatch` here and there must not be one: a second write path is how the two lists start
 * to diverge.
 *
 * Both calls need a session. There is no unauthenticated view of anybody's matches.
 */

const EVENT_KEY = (eventId: string): readonly unknown[] => ['matching', 'event', eventId];
const MINE_KEY = ['matching', 'mine'] as const;

/** `MEM-025` AC-1 — the event-scoped screen. */
export function useMyMatchSet(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<MatchSetResponse> {
  return useQuery({
    queryKey: EVENT_KEY(eventId),
    queryFn: () =>
      apiRequest<MatchSetResponse>(
        `/api/matching/events/${encodeURIComponent(eventId)}/mine`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

/** `MEM-025` AC-2 — the cross-event screen. */
export function useMyMatchSets(options: {
  readonly enabled: boolean;
}): UseQueryResult<MatchSetsResponse> {
  return useQuery({
    queryKey: MINE_KEY,
    queryFn: () => apiRequest<MatchSetsResponse>('/api/matching/mine'),
    enabled: options.enabled,
    retry: false,
  });
}
